import { forwardRef, useCallback, useEffect, useState } from "react";

import { cn } from "src/shadcn/lib/utils";

import { PERIODS } from "./constants";
import { TimeColumn } from "./TimeColumn";
import type { TimePickerPanelProps, TimeValue } from "./types";
import { generateRange, to12Hour, to24Hour } from "./utils";

const TimePickerPanel = forwardRef<HTMLDivElement, TimePickerPanelProps>(
  (
    {
      value,
      onChange,
      format = "24",
      showSeconds = false,
      minuteStep = 1,
      secondStep = 1,
      disabled = false,
      className,
    },
    ref
  ) => {
    const getInitialTime = useCallback(
      (): TimeValue => ({
        hours: value?.hours ?? 0,
        minutes: value?.minutes ?? 0,
        seconds: value?.seconds ?? 0,
      }),
      [value]
    );

    const [internalTime, setInternalTime] = useState<TimeValue>(getInitialTime);

    useEffect(() => {
      setInternalTime(getInitialTime());
    }, [getInitialTime]);

    const hours = format === "12" ? generateRange(1, 12) : generateRange(0, 23);
    const minutes = generateRange(0, 59, minuteStep);
    const seconds = generateRange(0, 59, secondStep);

    const { hour12, period } =
      format === "12"
        ? to12Hour(internalTime.hours)
        : { hour12: internalTime.hours, period: "AM" as const };

    const handleHourSelect = (hourValue: number | string) => {
      const hour = Number(hourValue);
      const newHours = format === "12" ? to24Hour(hour, period) : hour;
      const newTime = { ...internalTime, hours: newHours };
      setInternalTime(newTime);
      onChange?.(newTime);
    };

    const handleMinuteSelect = (minuteValue: number | string) => {
      const newTime = { ...internalTime, minutes: Number(minuteValue) };
      setInternalTime(newTime);
      onChange?.(newTime);
    };

    const handleSecondSelect = (secondValue: number | string) => {
      const newTime = { ...internalTime, seconds: Number(secondValue) };
      setInternalTime(newTime);
      onChange?.(newTime);
    };

    const handlePeriodSelect = (newPeriod: number | string) => {
      const newHours = to24Hour(hour12, newPeriod as "AM" | "PM");
      const newTime = { ...internalTime, hours: newHours };
      setInternalTime(newTime);
      onChange?.(newTime);
    };

    return (
      <div
        ref={ref}
        className={cn("flex", className)}
        role="group"
        aria-label="Time picker"
      >
        <TimeColumn
          items={hours}
          selected={format === "12" ? hour12 : internalTime.hours}
          onSelect={handleHourSelect}
          disabled={disabled}
        />
        <div className="border-inline-end border-border" />
        <TimeColumn
          items={minutes}
          selected={internalTime.minutes}
          onSelect={handleMinuteSelect}
          disabled={disabled}
        />
        {showSeconds && (
          <>
            <div className="border-inline-end border-border" />
            <TimeColumn
              items={seconds}
              selected={internalTime.seconds ?? 0}
              onSelect={handleSecondSelect}
              disabled={disabled}
            />
          </>
        )}
        {format === "12" && (
          <>
            <div className="border-inline-end border-border" />
            <TimeColumn
              items={[...PERIODS]}
              selected={period}
              onSelect={handlePeriodSelect}
              disabled={disabled}
              padStart={0}
            />
          </>
        )}
      </div>
    );
  }
);

TimePickerPanel.displayName = "TimePickerPanel";

export { TimePickerPanel };
